/** Wire protocol for the BWWK peer-to-peer match transport. */

export const PROTOCOL_VERSION = 1 as const;
export const LOGICAL_TICK_RATE = 120 as const;
export const POSE_INTERVAL_TICKS = 18 as const;
export const DEFAULT_ATTACK_DELAY_TICKS = 60 as const;
export const DEFAULT_CHECKPOINT_INTERVAL_TICKS = 240 as const;

export type ProtocolVersion = typeof PROTOCOL_VERSION;
export type MatchTick = number;
export type SequenceNumber = number;

export interface PeerCapabilities {
  readonly poseChannel: boolean;
  readonly checkpoints: boolean;
  readonly reconnect: boolean;
  readonly seedCommitReveal: boolean;
  readonly [capability: string]: boolean | number | string;
}

export interface HelloMessage {
  readonly type: "hello";
  readonly rulesHash: string;
  readonly capabilities: PeerCapabilities;
  readonly nonceCommit: string;
}

export interface SeedRevealMessage {
  readonly type: "seed-reveal";
  readonly nonce: string;
}

/**
 * `startTimeMs` is expressed on `coordinatorId`'s monotonic clock. The peer
 * converts it with its clock estimate. An accept message must echo the exact
 * proposal fields.
 */
export interface ReadyMessage {
  readonly type: "ready";
  readonly manifestHash: string;
  readonly startTick: MatchTick;
  readonly startTimeMs: number;
  readonly coordinatorId: string;
  readonly proposalId: string;
  readonly agreement: "propose" | "accept";
}

export interface InputBatchMessage {
  readonly type: "input-batch";
  readonly firstTick: MatchTick;
  /** Compact application-defined input bytes, encoded as base64url. */
  readonly packedInputs: string;
  readonly inputSeq: SequenceNumber;
}

export interface PiecePoseMessage {
  readonly type: "piece-pose";
  readonly poseTick: MatchTick;
  readonly pieceIndex: number;
  readonly x: number;
  readonly y: number;
  readonly rotation: number;
}

export interface LockCommitMessage {
  readonly type: "lock-commit";
  readonly lockSeq: SequenceNumber;
  readonly lockTick: MatchTick;
  /** Canonical row-major 10 x 24 board snapshot. */
  readonly board: readonly number[];
  readonly boardHash: string;
  readonly clearedLines: number;
  readonly attackLines: number;
  readonly garbageApplied: number;
  readonly blockIndex: number;
  readonly holeIndex: number;
  readonly nextPiece: number;
}

export interface AttackMessage {
  readonly type: "attack";
  /** Must be `${senderId}:${sourceLockSeq}`. */
  readonly attackId: string;
  readonly sourceLockSeq: SequenceNumber;
  readonly createdTick: MatchTick;
  readonly eligibleTick: MatchTick;
  readonly lines: number;
}

export interface CheckpointMessage {
  readonly type: "checkpoint";
  readonly checkpointTick: MatchTick;
  /** Canonical state bytes, encoded as base64url. */
  readonly stateBytes: string;
  readonly stateHash: string;
  readonly lastInputSeq: SequenceNumber;
  readonly lastLockSeq: SequenceNumber;
}

export interface TopOutMessage {
  readonly type: "top-out";
  readonly topOutTick: MatchTick;
  readonly finalStateHash: string;
  readonly lastLockSeq: SequenceNumber;
}

export interface ResultAckMessage {
  readonly type: "result-ack";
  readonly topOutSenderId: string;
  readonly topOutTick: MatchTick;
  readonly finalStateHash: string;
  readonly accepted: boolean;
  readonly result: "win" | "loss" | "draw" | "no-contest";
  readonly reason?: string;
}

export interface ReconnectHelloMessage {
  readonly type: "reconnect-hello";
  readonly lastSeq: SequenceNumber;
  readonly lastConfirmedTick: MatchTick;
  readonly checkpointHash: string;
}

export interface ResumeMessage {
  readonly type: "resume";
  readonly checkpointTick: MatchTick;
  readonly resumeTick: MatchTick;
  readonly resumeTimeMs: number;
  readonly coordinatorId: string;
  readonly transcriptHash: string;
}

export interface RematchMessage {
  readonly type: "rematch";
  readonly action: "request" | "accept" | "decline";
  readonly round: number;
  readonly proposedMatchId?: string;
}

export interface HeartbeatMessage {
  readonly type: "heartbeat";
  readonly sentAtMs: number;
}

export interface PingMessage {
  readonly type: "ping";
  readonly pingId: string;
  readonly localSendMs: number;
}

export interface PongMessage {
  readonly type: "pong";
  readonly pingId: string;
  readonly localSendMs: number;
  readonly remoteReceiveMs: number;
  readonly remoteSendMs: number;
}

export type ControlMessage =
  | HelloMessage
  | SeedRevealMessage
  | ReadyMessage
  | InputBatchMessage
  | LockCommitMessage
  | AttackMessage
  | CheckpointMessage
  | TopOutMessage
  | ResultAckMessage
  | ReconnectHelloMessage
  | ResumeMessage
  | RematchMessage
  | HeartbeatMessage;

export type PoseChannelMessage = PiecePoseMessage | PingMessage | PongMessage;
export type MatchMessage = ControlMessage | PoseChannelMessage;

/**
 * Sequence numbers are per channel. `ack` always acknowledges the reliable
 * control channel, so a lost pose packet can never block or falsely advance a
 * reliable acknowledgement.
 */
export interface MatchEnvelope<M extends MatchMessage = MatchMessage> {
  readonly protocolVersion: ProtocolVersion;
  readonly matchId: string;
  readonly senderId: string;
  readonly seq: SequenceNumber;
  readonly ack: SequenceNumber;
  readonly tick: MatchTick;
  readonly message: M;
}

export type ControlEnvelope = MatchEnvelope<ControlMessage>;
export type PoseEnvelope = MatchEnvelope<PoseChannelMessage>;

export type ChannelKind = "control" | "pose";

export class ProtocolDecodeError extends Error {
  readonly causeValue: unknown;

  constructor(message: string, causeValue?: unknown) {
    super(message);
    this.name = "ProtocolDecodeError";
    this.causeValue = causeValue;
  }
}

const CONTROL_TYPES = new Set<MatchMessage["type"]>([
  "hello",
  "seed-reveal",
  "ready",
  "input-batch",
  "lock-commit",
  "attack",
  "checkpoint",
  "top-out",
  "result-ack",
  "reconnect-hello",
  "resume",
  "rematch",
  "heartbeat",
]);

const POSE_TYPES = new Set<MatchMessage["type"]>([
  "piece-pose",
  "ping",
  "pong",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isInteger(value: unknown): value is number {
  return isFiniteNumber(value) && Number.isSafeInteger(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return isInteger(value) && value >= 0;
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function hasString(value: Record<string, unknown>, key: string): boolean {
  return isString(value[key]) && (value[key] as string).length > 0;
}

function hasFinite(value: Record<string, unknown>, key: string): boolean {
  return isFiniteNumber(value[key]);
}

function hasInteger(value: Record<string, unknown>, key: string): boolean {
  return isInteger(value[key]);
}

function hasNonNegativeInteger(value: Record<string, unknown>, key: string): boolean {
  return isNonNegativeInteger(value[key]);
}

function isCapabilities(value: unknown): value is PeerCapabilities {
  if (!isRecord(value)) return false;
  if (
    !isBoolean(value.poseChannel) ||
    !isBoolean(value.checkpoints) ||
    !isBoolean(value.reconnect) ||
    !isBoolean(value.seedCommitReveal)
  ) {
    return false;
  }
  return Object.values(value).every(
    (entry) => isBoolean(entry) || isFiniteNumber(entry) || isString(entry),
  );
}

function isBoard(value: unknown): value is readonly number[] {
  return (
    Array.isArray(value) &&
    value.length === 240 &&
    value.every((cell) => isNonNegativeInteger(cell))
  );
}

function isMessage(value: unknown): value is MatchMessage {
  if (!isRecord(value) || !isString(value.type)) return false;

  switch (value.type) {
    case "hello":
      return (
        hasString(value, "rulesHash") &&
        isCapabilities(value.capabilities) &&
        hasString(value, "nonceCommit")
      );
    case "seed-reveal":
      return hasString(value, "nonce");
    case "ready":
      return (
        hasString(value, "manifestHash") &&
        hasNonNegativeInteger(value, "startTick") &&
        hasFinite(value, "startTimeMs") &&
        hasString(value, "coordinatorId") &&
        hasString(value, "proposalId") &&
        (value.agreement === "propose" || value.agreement === "accept")
      );
    case "input-batch":
      return (
        hasNonNegativeInteger(value, "firstTick") &&
        isString(value.packedInputs) &&
        hasNonNegativeInteger(value, "inputSeq")
      );
    case "piece-pose":
      return (
        hasNonNegativeInteger(value, "poseTick") &&
        hasNonNegativeInteger(value, "pieceIndex") &&
        hasInteger(value, "x") &&
        hasInteger(value, "y") &&
        hasNonNegativeInteger(value, "rotation")
      );
    case "lock-commit":
      return (
        hasNonNegativeInteger(value, "lockSeq") &&
        hasNonNegativeInteger(value, "lockTick") &&
        isBoard(value.board) &&
        hasString(value, "boardHash") &&
        hasNonNegativeInteger(value, "clearedLines") &&
        hasNonNegativeInteger(value, "attackLines") &&
        hasNonNegativeInteger(value, "garbageApplied") &&
        hasNonNegativeInteger(value, "blockIndex") &&
        hasNonNegativeInteger(value, "holeIndex") &&
        hasNonNegativeInteger(value, "nextPiece")
      );
    case "attack":
      return (
        hasString(value, "attackId") &&
        hasNonNegativeInteger(value, "sourceLockSeq") &&
        hasNonNegativeInteger(value, "createdTick") &&
        hasNonNegativeInteger(value, "eligibleTick") &&
        hasNonNegativeInteger(value, "lines")
      );
    case "checkpoint":
      return (
        hasNonNegativeInteger(value, "checkpointTick") &&
        isString(value.stateBytes) &&
        hasString(value, "stateHash") &&
        hasNonNegativeInteger(value, "lastInputSeq") &&
        hasNonNegativeInteger(value, "lastLockSeq")
      );
    case "top-out":
      return (
        hasNonNegativeInteger(value, "topOutTick") &&
        hasString(value, "finalStateHash") &&
        hasNonNegativeInteger(value, "lastLockSeq")
      );
    case "result-ack":
      return (
        hasString(value, "topOutSenderId") &&
        hasNonNegativeInteger(value, "topOutTick") &&
        hasString(value, "finalStateHash") &&
        isBoolean(value.accepted) &&
        (value.result === "win" ||
          value.result === "loss" ||
          value.result === "draw" ||
          value.result === "no-contest") &&
        (value.reason === undefined || isString(value.reason))
      );
    case "reconnect-hello":
      return (
        hasNonNegativeInteger(value, "lastSeq") &&
        hasNonNegativeInteger(value, "lastConfirmedTick") &&
        isString(value.checkpointHash)
      );
    case "resume":
      return (
        hasNonNegativeInteger(value, "checkpointTick") &&
        hasNonNegativeInteger(value, "resumeTick") &&
        hasFinite(value, "resumeTimeMs") &&
        hasString(value, "coordinatorId") &&
        hasString(value, "transcriptHash")
      );
    case "rematch":
      return (
        (value.action === "request" || value.action === "accept" || value.action === "decline") &&
        hasNonNegativeInteger(value, "round") &&
        (value.proposedMatchId === undefined || hasString(value, "proposedMatchId"))
      );
    case "heartbeat":
      return hasFinite(value, "sentAtMs");
    case "ping":
      return hasString(value, "pingId") && hasFinite(value, "localSendMs");
    case "pong":
      return (
        hasString(value, "pingId") &&
        hasFinite(value, "localSendMs") &&
        hasFinite(value, "remoteReceiveMs") &&
        hasFinite(value, "remoteSendMs")
      );
    default:
      return false;
  }
}

export function channelForMessage(message: MatchMessage): ChannelKind {
  if (CONTROL_TYPES.has(message.type)) return "control";
  if (POSE_TYPES.has(message.type)) return "pose";
  throw new ProtocolDecodeError(`Unsupported message type: ${String(message.type)}`);
}

export function isControlMessage(message: MatchMessage): message is ControlMessage {
  return CONTROL_TYPES.has(message.type);
}

export function isPoseChannelMessage(message: MatchMessage): message is PoseChannelMessage {
  return POSE_TYPES.has(message.type);
}

export function makeEnvelope<M extends MatchMessage>(params: {
  readonly matchId: string;
  readonly senderId: string;
  readonly seq: SequenceNumber;
  readonly ack: SequenceNumber;
  readonly tick: MatchTick;
  readonly message: M;
}): MatchEnvelope<M> {
  if (!params.matchId || !params.senderId) {
    throw new ProtocolDecodeError("matchId and senderId are required");
  }
  if (
    !isNonNegativeInteger(params.seq) ||
    !isNonNegativeInteger(params.ack) ||
    !isNonNegativeInteger(params.tick)
  ) {
    throw new ProtocolDecodeError("seq, ack, and tick must be non-negative safe integers");
  }
  if (!isMessage(params.message)) {
    throw new ProtocolDecodeError("Invalid outgoing protocol message", params.message);
  }
  return { protocolVersion: PROTOCOL_VERSION, ...params };
}

export function parseEnvelope(value: unknown): MatchEnvelope {
  if (!isRecord(value)) {
    throw new ProtocolDecodeError("Envelope must be an object", value);
  }
  if (value.protocolVersion !== PROTOCOL_VERSION) {
    throw new ProtocolDecodeError(
      `Unsupported protocol version: ${String(value.protocolVersion)}`,
      value,
    );
  }
  if (
    !hasString(value, "matchId") ||
    !hasString(value, "senderId") ||
    !hasNonNegativeInteger(value, "seq") ||
    !hasNonNegativeInteger(value, "ack") ||
    !hasNonNegativeInteger(value, "tick") ||
    !isMessage(value.message)
  ) {
    throw new ProtocolDecodeError("Malformed protocol envelope", value);
  }
  return value as unknown as MatchEnvelope;
}

export function serializeEnvelope(envelope: MatchEnvelope): string {
  return JSON.stringify(parseEnvelope(envelope));
}

export function deserializeEnvelope(data: string | ArrayBuffer | ArrayBufferView): MatchEnvelope {
  try {
    let text: string;
    if (typeof data === "string") {
      text = data;
    } else if (ArrayBuffer.isView(data)) {
      text = new TextDecoder().decode(
        new Uint8Array(data.buffer, data.byteOffset, data.byteLength),
      );
    } else {
      text = new TextDecoder().decode(new Uint8Array(data));
    }
    return parseEnvelope(JSON.parse(text) as unknown);
  } catch (error) {
    if (error instanceof ProtocolDecodeError) throw error;
    throw new ProtocolDecodeError("Could not decode protocol envelope", error);
  }
}

export async function deserializeDataChannelEnvelope(data: unknown): Promise<MatchEnvelope> {
  if (typeof data === "string" || data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
    return deserializeEnvelope(data);
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return deserializeEnvelope(await data.arrayBuffer());
  }
  throw new ProtocolDecodeError("Unsupported DataChannel payload", data);
}

export function attackIdFor(senderId: string, sourceLockSeq: number): string {
  if (!senderId || !isNonNegativeInteger(sourceLockSeq)) {
    throw new ProtocolDecodeError("Invalid attack id components");
  }
  return `${senderId}:${sourceLockSeq}`;
}

export function validateAttackIdentity(
  envelope: MatchEnvelope<AttackMessage>,
): boolean {
  return envelope.message.attackId === attackIdFor(
    envelope.senderId,
    envelope.message.sourceLockSeq,
  );
}
